import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  TagIcon,
  BookOpenIcon,
  LinkIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  PlayIcon,
  VideoCameraIcon,
  EyeIcon,
  ChartBarIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  increment,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { useRole } from '../../../hooks/useRole';
import { useToast } from '../../../context/ToastContext';
import { DeleteConfirmation } from '../../ui/DeleteConfirmation';
import { Card, CardContent } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Input, Textarea, Label, Select } from '../../ui/Input';
import { Modal, ModalFooter } from '../../ui/Modal';

const CoachingTools = () => {
  const { currentUser } = useAuth();
  const { userRole } = useRole();
  const toast = useToast();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolToDelete, setToolToDelete] = useState(null);
  const [editingTool, setEditingTool] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [savingTool, setSavingTool] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [newTool, setNewTool] = useState({
    title: '',
    description: '',
    category: 'framework',
    link: '',
    tags: [],
    content: '',
    videoUrl: '',
    steps: [],
  });

  const isCoach = userRole === 'coach' || userRole === 'admin' || userRole === 'superAdmin';

  useEffect(() => {
    setLoading(true);
    let unsubscribe = null;

    // Helper function to process snapshot data
    const processSnapshot = snapshot => {
      const toolsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle Firestore timestamp conversion
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt?.seconds
              ? new Date(data.createdAt.seconds * 1000)
              : data.createdAt || new Date(),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt?.seconds
              ? new Date(data.updatedAt.seconds * 1000)
              : data.updatedAt || new Date(),
        };
      });

      // Sort manually by createdAt (descending)
      toolsData.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      });

      setTools(toolsData);
      setLoading(false);
    };

    // Try query with orderBy first
    const tryQueryWithOrderBy = () => {
      const toolsQuery = query(
        collection(firestore, 'coachingTools'),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(toolsQuery, processSnapshot, error => {
        console.error('Error listening to coaching tools:', error);

        // If orderBy fails due to missing index, try without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.info('Index not yet created, using fallback query');
          tryQueryWithoutOrderBy();
        } else {
          toast.error('Failed to load coaching tools', 'Error');
          setLoading(false);
        }
      });
    };

    // Fallback query without orderBy
    const tryQueryWithoutOrderBy = () => {
      if (unsubscribe) unsubscribe(); // Clean up previous listener

      const simpleQuery = query(collection(firestore, 'coachingTools'));

      unsubscribe = onSnapshot(simpleQuery, processSnapshot, fallbackError => {
        console.error('Error in fallback query:', fallbackError);
        toast.error('Failed to load coaching tools', 'Error');
        setLoading(false);
      });
    };

    // Start with orderBy query
    tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const categories = [
    { id: 'all', name: 'All Categories', color: 'gray' },
    { id: 'framework', name: 'Frameworks', color: 'indigo' },
    { id: 'assessment', name: 'Assessments', color: 'blue' },
    { id: 'worksheet', name: 'Worksheets', color: 'emerald' },
    { id: 'template', name: 'Templates', color: 'purple' },
    { id: 'guide', name: 'Guides', color: 'orange' },
  ];

  const getCategoryColor = categoryId => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return 'gray';

    const colorMap = {
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
    };

    return colorMap[category.color] || colorMap.gray;
  };

  // Helper function to normalize step data (handle backward compatibility)
  const normalizeStep = step => {
    if (typeof step === 'string') {
      return { title: step, description: '' };
    }
    return {
      title: step?.title || '',
      description: step?.description || '',
    };
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newTool.title || !newTool.description) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    setSavingTool(true);
    try {
      // Fetch user profile to get firstname and lastname
      let authorName = currentUser.displayName || currentUser.email;
      let authorFirstName = '';
      let authorLastName = '';

      try {
        // Try to get coach profile first
        const coachDoc = await getDoc(doc(firestore, 'coaches', currentUser.uid));
        if (coachDoc.exists()) {
          const coachData = coachDoc.data();
          authorFirstName = coachData.firstname || '';
          authorLastName = coachData.lastname || '';
          if (authorFirstName || authorLastName) {
            authorName = `${authorFirstName} ${authorLastName}`.trim();
          }
        } else {
          // Try admin collection
          const adminDoc = await getDoc(doc(firestore, 'admin', currentUser.uid));
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            authorFirstName = adminData.firstname || '';
            authorLastName = adminData.lastname || '';
            if (authorFirstName || authorLastName) {
              authorName = `${authorFirstName} ${authorLastName}`.trim();
            }
          } else {
            // Try moderator collection
            const moderatorDoc = await getDoc(doc(firestore, 'moderator', currentUser.uid));
            if (moderatorDoc.exists()) {
              const moderatorData = moderatorDoc.data();
              authorFirstName = moderatorData.firstname || '';
              authorLastName = moderatorData.lastname || '';
              if (authorFirstName || authorLastName) {
                authorName = `${authorFirstName} ${authorLastName}`.trim();
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch user profile:', error);
        // Continue with email as fallback
      }

      let videoUrl = newTool.videoUrl;

      // Upload video if a new file is selected
      if (videoFile) {
        setUploadingVideo(true);
        try {
          const videoRef = ref(storage, `coachingTools/videos/${Date.now()}_${videoFile.name}`);
          await uploadBytes(videoRef, videoFile);
          videoUrl = await getDownloadURL(videoRef);

          // Delete old video if editing and replacing
          if (editingTool?.videoUrl && editingTool.videoUrl !== newTool.videoUrl) {
            try {
              const oldVideoRef = ref(storage, editingTool.videoUrl);
              await deleteObject(oldVideoRef);
            } catch (error) {
              console.warn('Failed to delete old video:', error);
            }
          }
        } catch (error) {
          console.error('Error uploading video:', error);
          toast.error('Failed to upload video', 'Error');
          setUploadingVideo(false);
          setSavingTool(false);
          return;
        }
        setUploadingVideo(false);
      }

      const toolData = {
        ...newTool,
        videoUrl: videoUrl || newTool.videoUrl || '',
        createdBy: currentUser.email,
        createdByName: authorName,
        createdByFirstName: authorFirstName,
        createdByLastName: authorLastName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Initialize analytics
        views: editingTool?.views || 0,
        clicks: editingTool?.clicks || 0,
        lastViewedAt: editingTool?.lastViewedAt || null,
      };

      if (editingTool) {
        await updateDoc(doc(firestore, 'coachingTools', editingTool.id), {
          ...toolData,
          updatedAt: serverTimestamp(),
        });
        toast.success('Coaching tool updated successfully!');
      } else {
        await addDoc(collection(firestore, 'coachingTools'), toolData);
        toast.success('Coaching tool created successfully!');
      }

      setShowModal(false);
      setEditingTool(null);
      setNewTool({
        title: '',
        description: '',
        category: 'framework',
        link: '',
        tags: [],
        content: '',
        videoUrl: '',
        steps: [],
      });
      setVideoFile(null);
      setVideoPreview(null);
    } catch (error) {
      console.error('Error saving coaching tool:', error);
      toast.error('Failed to save coaching tool', 'Error');
    } finally {
      setSavingTool(false);
    }
  };

  const handleDeleteClick = tool => {
    setToolToDelete(tool);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!toolToDelete) return;

    try {
      // Delete video from storage if exists
      if (toolToDelete.videoUrl) {
        try {
          const videoRef = ref(storage, toolToDelete.videoUrl);
          await deleteObject(videoRef);
        } catch (error) {
          console.warn('Failed to delete video:', error);
          // Continue with tool deletion even if video deletion fails
        }
      }

      await deleteDoc(doc(firestore, 'coachingTools', toolToDelete.id));
      toast.success('Coaching tool deleted successfully!');
      setShowDeleteModal(false);
      setToolToDelete(null);
    } catch (error) {
      console.error('Error deleting coaching tool:', error);
      toast.error('Failed to delete coaching tool', 'Error');
    }
  };

  const handleEdit = tool => {
    setEditingTool(tool);
    // Normalize steps to ensure they're objects
    const normalizedSteps = (tool.steps || []).map(step => normalizeStep(step));
    setNewTool({
      title: tool.title || '',
      description: tool.description || '',
      category: tool.category || 'framework',
      link: tool.link || '',
      tags: tool.tags || [],
      content: tool.content || '',
      videoUrl: tool.videoUrl || '',
      steps: normalizedSteps,
    });
    setVideoFile(null);
    setVideoPreview(tool.videoUrl || null);
    setShowModal(true);
  };

  const handleCardClick = async tool => {
    setSelectedTool(tool);
    setShowDetailModal(true);

    // Track view analytics
    try {
      const toolRef = doc(firestore, 'coachingTools', tool.id);
      await updateDoc(toolRef, {
        views: increment(1),
        lastViewedAt: serverTimestamp(),
      });

      // Log detailed analytics event
      await addDoc(collection(firestore, 'coachingToolsAnalytics'), {
        toolId: tool.id,
        toolTitle: tool.title,
        userId: currentUser?.uid || 'anonymous',
        userEmail: currentUser?.email || 'anonymous',
        userRole: userRole || 'unknown',
        action: 'view',
        timestamp: serverTimestamp(),
        metadata: {
          category: tool.category,
          hasVideo: !!tool.videoUrl,
          hasContent: !!tool.content,
          hasLink: !!tool.link,
        },
      });
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  const handleVideoUpload = e => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a valid video file', 'Error');
        return;
      }

      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video file size must be less than 100MB', 'Error');
        return;
      }

      setVideoFile(file);
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
    }
  };

  const addStep = () => {
    setNewTool({
      ...newTool,
      steps: [...(newTool.steps || []), { title: '', description: '' }],
    });
  };

  const updateStep = (index, field, value) => {
    const updatedSteps = [...(newTool.steps || [])];
    // Handle backward compatibility - convert string steps to objects
    if (typeof updatedSteps[index] === 'string') {
      updatedSteps[index] = { title: updatedSteps[index], description: '' };
    }
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value,
    };
    setNewTool({ ...newTool, steps: updatedSteps });
  };

  const removeStep = index => {
    const updatedSteps = newTool.steps.filter((_, i) => i !== index);
    setNewTool({ ...newTool, steps: updatedSteps });
  };

  const filteredTools = tools.filter(tool => {
    const matchesSearch =
      tool.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary/20 border-t-primary"></div>
              <p className="font-medium text-text-secondary">Loading coaching tools...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
              <SparklesIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Coaching Tools</h2>
              <p className="text-sm text-gray-500">Professional coaching tools and methodologies</p>
            </div>
          </div>
          {isCoach && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setEditingTool(null);
                setNewTool({
                  title: '',
                  description: '',
                  category: 'framework',
                  link: '',
                  tags: [],
                  content: '',
                  videoUrl: '',
                  steps: [],
                });
                setVideoFile(null);
                setVideoPreview(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-indigo-600 hover:to-purple-700 hover:shadow-xl"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Add Tool</span>
            </motion.button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search coaching tools..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 pl-10 text-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* View Mode Toggle */}
          <div className="flex rounded-xl border border-gray-300 bg-white p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <TableCellsIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

        {/* Tools View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filteredTools.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{
                    delay: index * 0.05,
                    layout: { duration: 0.3 },
                  }}
                  onClick={() => handleCardClick(tool)}
                  className="group relative cursor-pointer overflow-hidden rounded-xl border border-border bg-surface shadow-sm transition-all hover:scale-[1.02] hover:border-primary/20 hover:shadow-lg"
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 transition-opacity group-hover:opacity-100" />

                  <div className="relative p-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
                        <SparklesIcon className="h-6 w-6 text-white" />
                      </div>
                      {isCoach && (
                        <div className="flex items-center gap-1">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              handleEdit(tool);
                            }}
                            className="rounded-lg p-2 text-text-tertiary transition-colors hover:bg-surface-elevated hover:text-primary"
                            aria-label="Edit tool"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteClick(tool);
                            }}
                            className="rounded-lg p-2 text-text-tertiary transition-colors hover:bg-error/10 hover:text-error"
                            aria-label="Delete tool"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </motion.button>
                        </div>
                      )}
                    </div>

                    <h3 className="mb-2 text-xl font-bold text-text-primary transition-colors group-hover:text-primary">
                      {tool.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-text-secondary">
                      {tool.description}
                    </p>

                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(tool.category)}`}
                      >
                        <TagIcon className="h-3 w-3" />
                        {categories.find(c => c.id === tool.category)?.name || tool.category}
                      </span>
                      {tool.createdAt && (
                        <div className="flex items-center gap-1 text-xs text-text-tertiary">
                          <ClockIcon className="h-3 w-3" />
                          <span>
                            {tool.createdAt?.toDate
                              ? new Date(tool.createdAt.toDate()).toLocaleDateString()
                              : 'Recently'}
                          </span>
                        </div>
                      )}
                    </div>

                    {tool.tags && tool.tags.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-1.5">
                        {tool.tags.slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} variant="outline" size="sm">
                            {tag}
                          </Badge>
                        ))}
                        {tool.tags.length > 3 && (
                          <Badge variant="outline" size="sm">
                            +{tool.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {tool.createdBy && (
                      <div className="mb-4 flex items-center gap-2 text-xs text-text-tertiary">
                        <UserIcon className="h-3 w-3" />
                        <span>
                          By{' '}
                          {tool.createdByFirstName || tool.createdByLastName
                            ? `${tool.createdByFirstName || ''} ${tool.createdByLastName || ''}`.trim()
                            : tool.createdByName || ''}{' '}
                          ({tool.createdBy})
                        </span>
                      </div>
                    )}

                    {/* Analytics Badge */}
                    {(tool.views > 0 || tool.clicks > 0) && (
                      <div className="mb-4 flex items-center gap-3 text-xs text-text-tertiary">
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-3 w-3" />
                          <span>{tool.views || 0} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ChartBarIcon className="h-3 w-3" />
                          <span>{tool.clicks || 0} clicks</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {tool.videoUrl && (
                        <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                          <PlayIcon className="h-4 w-4 text-indigo-600" />
                          <span className="text-xs font-medium text-indigo-700">
                            Video available
                          </span>
                        </div>
                      )}
                      {tool.content && (
                        <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-elevated px-3 py-2">
                          <DocumentTextIcon className="h-4 w-4 text-primary" />
                          <span className="text-xs font-medium text-text-secondary">
                            Detailed content
                          </span>
                        </div>
                      )}
                      {tool.link && (
                        <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                          <LinkIcon className="h-4 w-4 text-indigo-600" />
                          <span className="text-xs font-medium text-indigo-700">External link</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Category
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Author
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Analytics
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Date
                    </th>
                    {isCoach && (
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <AnimatePresence>
                    {filteredTools.map((tool, index) => (
                      <motion.tr
                        key={tool.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.02 }}
                        onClick={() => handleCardClick(tool)}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                              <SparklesIcon className="h-5 w-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-gray-900">
                                {tool.title}
                              </div>
                              {tool.description && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleCardClick(tool);
                                  }}
                                  className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
                                >
                                  See more
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(tool.category)}`}
                          >
                            <TagIcon className="h-3 w-3" />
                            {categories.find(c => c.id === tool.category)?.name || tool.category}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          {tool.createdBy && (
                            <div className="flex items-center gap-2">
                              <UserIcon className="h-4 w-4 text-gray-400" />
                              <span>
                                {tool.createdByFirstName || tool.createdByLastName
                                  ? `${tool.createdByFirstName || ''} ${tool.createdByLastName || ''}`.trim()
                                  : tool.createdByName || ''}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            {tool.videoUrl && (
                              <span className="flex items-center gap-1 text-xs text-indigo-600">
                                <VideoCameraIcon className="h-3 w-3" />
                                Video
                              </span>
                            )}
                            {tool.link && (
                              <span className="flex items-center gap-1 text-xs text-indigo-600">
                                <LinkIcon className="h-3 w-3" />
                                Link
                              </span>
                            )}
                            {tool.content && (
                              <span className="flex items-center gap-1 text-xs text-indigo-600">
                                <DocumentTextIcon className="h-3 w-3" />
                                Content
                              </span>
                            )}
                            {!tool.videoUrl && !tool.link && !tool.content && (
                              <span className="text-xs text-gray-400">Basic</span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                              <span>{tool.views || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ChartBarIcon className="h-4 w-4 text-gray-400" />
                              <span>{tool.clicks || 0}</span>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {tool.createdAt?.toDate
                            ? new Date(tool.createdAt.toDate()).toLocaleDateString()
                            : tool.createdAt instanceof Date
                              ? tool.createdAt.toLocaleDateString()
                              : 'Recently'}
                        </td>
                        {isCoach && (
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEdit(tool);
                                }}
                                className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                                aria-label="Edit tool"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteClick(tool);
                                }}
                                className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                aria-label="Delete tool"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </motion.button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredTools.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-16 text-center"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <BookOpenIcon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-text-primary">
              {searchTerm || selectedCategory !== 'all'
                ? 'No tools match your filters'
                : 'No coaching tools yet'}
            </h3>
            <p className="text-sm text-text-secondary">
              {searchTerm || selectedCategory !== 'all'
                ? 'Try adjusting your search or filters'
                : isCoach
                  ? 'Create your first coaching tool to get started!'
                  : 'Check back later for coaching tools'}
            </p>
          </motion.div>
        )}

        {/* Add/Edit Modal */}
        <Modal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingTool(null);
          }}
          title={editingTool ? 'Edit Coaching Tool' : 'Create Coaching Tool'}
          description="Add a new coaching tool to the library"
          size="lg"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="tool-title" required>
                Title
              </Label>
              <Input
                id="tool-title"
                type="text"
                value={newTool.title}
                onChange={e => setNewTool({ ...newTool, title: e.target.value })}
                placeholder="Enter tool title"
                required
              />
            </div>

            <div>
              <Label htmlFor="tool-description" required>
                Description
              </Label>
              <Textarea
                id="tool-description"
                value={newTool.description}
                onChange={e => setNewTool({ ...newTool, description: e.target.value })}
                rows={3}
                placeholder="Describe the coaching tool..."
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="tool-category">Category</Label>
                <Select
                  id="tool-category"
                  value={newTool.category}
                  onChange={e => setNewTool({ ...newTool, category: e.target.value })}
                >
                  {categories
                    .filter(c => c.id !== 'all')
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="tool-link">Link (Optional)</Label>
                <Input
                  id="tool-link"
                  type="url"
                  value={newTool.link}
                  onChange={e => setNewTool({ ...newTool, link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tool-content">Content (Optional)</Label>
              <Textarea
                id="tool-content"
                value={newTool.content}
                onChange={e => setNewTool({ ...newTool, content: e.target.value })}
                rows={5}
                placeholder="Add detailed content or instructions..."
              />
            </div>

            <div>
              <Label>Steps (Optional)</Label>
              <div className="space-y-4">
                {(newTool.steps || []).map((step, index) => {
                  const normalizedStep = normalizeStep(step);
                  return (
                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-sm font-bold text-indigo-600">
                            {index + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">
                            Step {index + 1}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStep(index)}
                          className="text-error hover:bg-error/10 hover:text-error"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label htmlFor={`step-title-${index}`} className="text-xs">
                            Step Title <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id={`step-title-${index}`}
                            type="text"
                            value={normalizedStep.title}
                            onChange={e => updateStep(index, 'title', e.target.value)}
                            placeholder={`e.g., Identify the problem`}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor={`step-description-${index}`} className="text-xs">
                            Description
                          </Label>
                          <Textarea
                            id={`step-description-${index}`}
                            value={normalizedStep.description}
                            onChange={e => updateStep(index, 'description', e.target.value)}
                            placeholder={`Describe what to do in this step...`}
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addStep}
                  className="w-full border-dashed"
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  Add Step
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="tool-video">Video (Optional)</Label>
              <div className="space-y-3">
                {videoPreview && (
                  <div className="relative rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <VideoCameraIcon className="h-5 w-5 text-indigo-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {videoFile?.name || 'Video uploaded'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {videoFile
                              ? `${(videoFile.size / (1024 * 1024)).toFixed(2)} MB`
                              : 'Existing video'}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setVideoFile(null);
                          setVideoPreview(null);
                          setNewTool({ ...newTool, videoUrl: '' });
                        }}
                        className="text-error hover:bg-error/10 hover:text-error"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    {videoPreview && !videoFile && (
                      <video
                        src={videoPreview}
                        controls
                        className="mt-3 w-full rounded-lg"
                        style={{ maxHeight: '300px' }}
                      />
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="video-upload"
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700 transition-all hover:border-indigo-500 hover:bg-indigo-50 hover:text-indigo-600"
                  >
                    <VideoCameraIcon className="h-5 w-5" />
                    <span>{videoFile ? 'Change Video' : 'Upload Video'}</span>
                  </label>
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  {uploadingVideo && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600"></div>
                      <span>Uploading...</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Supported formats: MP4, WebM, MOV. Max size: 100MB
                </p>
              </div>
            </div>

            <ModalFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setEditingTool(null);
                }}
                disabled={uploadingVideo || savingTool}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingVideo || savingTool}>
                {uploadingVideo || savingTool ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white"></div>
                    {uploadingVideo
                      ? 'Uploading video...'
                      : savingTool
                        ? 'Saving...'
                        : 'Processing...'}
                  </span>
                ) : editingTool ? (
                  'Update Tool'
                ) : (
                  'Create Tool'
                )}
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Detail Modal - Shows full tool details */}
        <AnimatePresence>
          {showDetailModal && selectedTool && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
              onClick={() => {
                setShowDetailModal(false);
                setSelectedTool(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
                className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
              >
                {/* Header */}
                <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(selectedTool.category)}`}
                        >
                          <TagIcon className="h-3 w-3" />
                          {categories.find(c => c.id === selectedTool.category)?.name ||
                            selectedTool.category}
                        </span>
                        {selectedTool.createdAt && (
                          <div className="flex items-center gap-1 text-xs text-white/80">
                            <ClockIcon className="h-3 w-3" />
                            <span>
                              {selectedTool.createdAt?.toDate
                                ? new Date(selectedTool.createdAt.toDate()).toLocaleDateString()
                                : selectedTool.createdAt instanceof Date
                                  ? selectedTool.createdAt.toLocaleDateString()
                                  : 'Recently'}
                            </span>
                          </div>
                        )}
                      </div>
                      <h2 className="text-2xl font-bold text-white">{selectedTool.title}</h2>
                      {selectedTool.createdBy && (
                        <p className="mt-2 text-sm text-white/90">
                          By{' '}
                          {selectedTool.createdByFirstName || selectedTool.createdByLastName
                            ? `${selectedTool.createdByFirstName || ''} ${selectedTool.createdByLastName || ''}`.trim()
                            : selectedTool.createdByName || ''}{' '}
                          ({selectedTool.createdBy})
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setSelectedTool(null);
                      }}
                      className="rounded-lg p-2 text-white/90 transition-colors hover:bg-white/20"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-[calc(90vh-200px)] overflow-y-auto px-6 py-6">
                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">Description</h3>
                    <p className="leading-relaxed text-gray-700">{selectedTool.description}</p>
                  </div>

                  {/* Video */}
                  {selectedTool.videoUrl && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <VideoCameraIcon className="h-5 w-5 text-indigo-600" />
                        Video
                      </h3>
                      <div className="relative overflow-hidden rounded-xl bg-black">
                        <video
                          src={selectedTool.videoUrl}
                          controls
                          className="w-full"
                          style={{ maxHeight: '500px' }}
                          onPlay={() => {
                            // Track video play analytics
                            const toolRef = doc(firestore, 'coachingTools', selectedTool.id);
                            updateDoc(toolRef, {
                              clicks: increment(1),
                            });
                            addDoc(collection(firestore, 'coachingToolsAnalytics'), {
                              toolId: selectedTool.id,
                              toolTitle: selectedTool.title,
                              userId: currentUser?.uid || 'anonymous',
                              userEmail: currentUser?.email || 'anonymous',
                              userRole: userRole || 'unknown',
                              action: 'video_play',
                              timestamp: serverTimestamp(),
                            }).catch(console.error);
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  )}

                  {/* Steps */}
                  {selectedTool.steps && selectedTool.steps.length > 0 && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
                        Steps ({selectedTool.steps.length})
                      </h3>
                      <div className="space-y-3">
                        {selectedTool.steps.map((step, idx) => {
                          const normalizedStep = normalizeStep(step);
                          return (
                            <div
                              key={idx}
                              className="flex items-start gap-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-4"
                            >
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <h4 className="mb-1 font-bold text-gray-900">
                                  {normalizedStep.title}
                                </h4>
                                {normalizedStep.description && (
                                  <p className="leading-relaxed text-gray-700">
                                    {normalizedStep.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Link */}
                  {selectedTool.link && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <LinkIcon className="h-5 w-5 text-indigo-600" />
                        External Link
                      </h3>
                      <a
                        href={selectedTool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
                        onClick={() => {
                          // Track link click analytics
                          const toolRef = doc(firestore, 'coachingTools', selectedTool.id);
                          updateDoc(toolRef, {
                            clicks: increment(1),
                          });
                          addDoc(collection(firestore, 'coachingToolsAnalytics'), {
                            toolId: selectedTool.id,
                            toolTitle: selectedTool.title,
                            userId: currentUser?.uid || 'anonymous',
                            userEmail: currentUser?.email || 'anonymous',
                            userRole: userRole || 'unknown',
                            action: 'link_click',
                            timestamp: serverTimestamp(),
                          }).catch(console.error);
                        }}
                      >
                        <LinkIcon className="h-4 w-4" />
                        Access Tool
                      </a>
                    </div>
                  )}

                  {/* Detailed Content */}
                  {selectedTool.content && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                        Detailed Content
                      </h3>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                          {selectedTool.content}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {selectedTool.tags && selectedTool.tags.length > 0 && (
                    <div className="mb-6">
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedTool.tags.map((tag, idx) => (
                          <Badge key={idx} variant="outline" size="sm">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Analytics */}
                  {(selectedTool.views > 0 || selectedTool.clicks > 0) && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <ChartBarIcon className="h-4 w-4" />
                        Analytics
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Total Views</p>
                          <p className="text-2xl font-bold text-indigo-600">
                            {selectedTool.views || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Total Clicks</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {selectedTool.clicks || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Actions */}
                {isCoach && (
                  <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                    <div className="flex items-center justify-end gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDetailModal(false);
                          handleEdit(selectedTool);
                        }}
                        leftIcon={<PencilIcon className="h-4 w-4" />}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDetailModal(false);
                          handleDeleteClick(selectedTool);
                        }}
                        className="border-error/20 text-error hover:bg-error/10"
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          open={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setToolToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Coaching Tool"
          message="Are you sure you want to delete this coaching tool? This action cannot be undone."
          itemName={toolToDelete?.title}
        />
      </CardContent>
    </Card>
  );
};

export default CoachingTools;
